import { ClusterCard, ClusterCardsParams, ClusterTable, ClusterTablesParams } from './clustersSlice'
import {
  ALL_CLUSTERS,
  GRAPHQL_API_FILTERS,
  LIMIT_DEFAULT,
  ORPHAN_CLUSTER_ID,
  PAGE
} from '../../constants'
import { getAfterCursor } from '../../utils/graphqlUtil'
import { gql } from 'graphql-request'

export const queryClusterCards = (params: ClusterCardsParams): string => {
  const { datasourceId, databaseId, clusterId } = params
  const dbQueryParams = databaseId ? `(id: "${databaseId}")` : ''
  const clusterParams = clusterId ? `(id: "${clusterId}")` : ''

  const dbClustersQuery = `
      databases${dbQueryParams}{
        edges {
          node {
            id
            name
            tableClusters${clusterParams} {
              edges {
                node {
                  metadata {
                    timestampColumnID
                  }
                  tables(first: 1) {
                    count
                  }
                  piiTables: tables(first: 1, toggleFilter: {key: ${GRAPHQL_API_FILTERS.isSensitive}}) {
                    count
                  }
                  piiViewTables: tables(
                    first: 1,
                    toggleFilter: {key: ${GRAPHQL_API_FILTERS.isSensitive}},
                    booleanFilter: [{ key: ${GRAPHQL_API_FILTERS.isView}, value: true }]
                  ) {
                    count
                  }
                  tablesPiiNeedReview: tables(first: 1, booleanFilter: [{key: ${GRAPHQL_API_FILTERS.isReviewed}, value: false}, {key: ${GRAPHQL_API_FILTERS.isSensitive}, value: true}]) {
                    count
                  }
                  columnsPiiNeedReview: columns(first: 1, booleanFilter: [{key: ${GRAPHQL_API_FILTERS.isReviewed}, value: false}, {key: ${GRAPHQL_API_FILTERS.isSensitive}, value: true}])  {
                    count
                  }
                  columns(first: 1) {
                    count
                  }
                  viewColumns: columns(booleanFilter: [{ key: ${GRAPHQL_API_FILTERS.isView}, value: true }]) {
                    count
                  }
                  piiColumns: columns(first: 1, toggleFilter: {key: ${GRAPHQL_API_FILTERS.isSensitive}}) {
                    count
                  }
                  viewPiiColumns: columns(
                    first: 1,
                    booleanFilter: [{ key: ${GRAPHQL_API_FILTERS.isView}, value: true }],
                    toggleFilter: {key: ${GRAPHQL_API_FILTERS.isSensitive}}
                  ) {
                    count
                  }
                  ... on AnchorTableCluster {
                    id
                    name
                    isView
                    isReviewed
                    entityScanDetails {
                      totalEntitiesCount
                      scannedEntitiesCount
                    }
                  }
                  ... on OrphanTableCluster{
                    orphanTableClusterId: id,
                    orphanTableClusterName: name
                  }
                }
              }
            }
          }
        }
      }
    `

  const dsClustersQuery = `
      datasources(first: 1, id: "${datasourceId}") {
        edges {
          node {
            ${dbClustersQuery}
          }
        }
      }
    `

  return gql`{
    ${params.databaseId ? dbClustersQuery : dsClustersQuery}
  }`
}
export const mapQueryClusterCards = (raw: any): ClusterCard[] => {
  try {
    const getCards = (clusterEdges, databaseId: string, databaseName: string) => {
      return clusterEdges
        .filter(({ node: cluster }) => !!(cluster?.viewColumns?.count || cluster.columns?.count))
        .map(({ node: cluster }) => {
          const card: ClusterCard = {
            databaseId,
            databaseName,
            clusterId: cluster.id || cluster.orphanTableClusterId || ORPHAN_CLUSTER_ID,
            clusterName:
              cluster.name || cluster.orphanTableClusterName || databaseName || ORPHAN_CLUSTER_ID,
            tablesCount: cluster.tables?.count || 0,
            tablesPiiCount: cluster.piiTables?.count || 0,
            viewsPiiCount: cluster.piiViewTables?.count || 0,
            tablesPiiNeedReviewCount: cluster.tablesPiiNeedReview?.count || 0,
            columnsPiiNeedReviewCount: cluster.columnsPiiNeedReview?.count || 0,
            columnsCount: cluster.columns?.count || 0,
            columnsPiiCount: cluster.piiColumns?.count || 0,
            viewColumnsPiiCount: cluster.viewPiiColumns?.count || 0,
            entitiesTotalCount: cluster.entityScanDetails?.totalEntitiesCount || 0,
            entitiesScannedCount: cluster.entityScanDetails?.scannedEntitiesCount || 0,
            isReviewed: cluster.isReviewed,
            isTimestampColumnConfigured: !!cluster?.metadata?.timestampColumnID,
            isView: cluster.isView
          }

          return card
        })
    }

    let cards = []
    if (raw.datasources) {
      cards = raw.datasources?.edges[0].node?.databases?.edges
        .map(({ node: db }) => getCards(db.tableClusters?.edges, db.id, db.name))
        .flat()
    } else {
      cards = getCards(
        raw.databases?.edges[0]?.node?.tableClusters?.edges,
        raw.databases?.edges[0]?.node?.id,
        raw.databases?.edges[0]?.node?.name
      )
    }

    return cards
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryClusterTables = (params: ClusterTablesParams): string => {
  const { pageSize = LIMIT_DEFAULT, datasourceId, databaseId, clusterId } = params
  const cursor = getAfterCursor(params[PAGE], pageSize)

  const tablesQuery = `
  tables(first: ${pageSize}, after: "${cursor}", toggleFilter: { key:${GRAPHQL_API_FILTERS.isSensitive} }) {
      count
      edges {
        node {
          id
          name
          lastModifiedOn
          createdOn
          rowCount
          clusterRootTableUrn
          columns {
            count
          }
          columnsPii: columns(toggleFilter: { key: ${GRAPHQL_API_FILTERS.isSensitive} }) {
            count
          }
          columnsNoScan: columns(booleanFilter: {key: ${GRAPHQL_API_FILTERS.isNoScan}, value: true}) {
            count
          }
          columnsNeedReview: columns(booleanFilter: {key: ${GRAPHQL_API_FILTERS.isReviewed}, value: false}) {
            count
          }
          schema {
            edges {
              node {
                id
                name
              }
            }
          }
        }
      }
    }
  `

  const allClusterTablesQuery = `... on AnchorTableCluster {${tablesQuery}}`
  const orphanTablesQuery = `... on OrphanTableCluster {${tablesQuery}}`

  const clusterQuery = `
  tableClusters${clusterId !== ALL_CLUSTERS ? `(id: "${clusterId}")` : ''}{
    edges {
      node {
        ${clusterId === ORPHAN_CLUSTER_ID ? orphanTablesQuery : allClusterTablesQuery}
        ... on AnchorTableCluster {
          id
          name
        }
        ... on OrphanTableCluster{
          orphanTableClusterId: id,
          orphanTableClusterName: name
        }
      }
    }
  }`

  const dbQueryParams = databaseId ? `(id: "${databaseId}")` : ''

  const dbQuery = `
  databases${dbQueryParams}{
    edges {
      node {
        id
        name
        ${clusterId ? clusterQuery : tablesQuery}
      }
    }
  }`

  const dsQuery = `
  datasources(id: "${datasourceId}") {
    edges {
      node {
        ${dbQuery}
      }
    }
  }`

  return gql`{${databaseId ? dbQuery : dsQuery}}`
}
export const mapQueryClusterTables = (raw: any): { list: ClusterTable[]; total: number } => {
  try {
    const mapTablesList = ({
      tables,
      databaseId,
      databaseName,
      clusterId = '',
      clusterName = ''
    }) => {
      return tables.edges?.map(({ node: table }) => ({
        databaseId,
        databaseName,
        clusterId,
        clusterName,
        tableId: table.id || '',
        tableName: table.name || '',
        rowsCount: table.rowCount || 0,
        isReviewed: (table.columnsNeedReview?.count || 0) === 0,
        parentId: table.clusterRootTableUrn || '',
        lastModified: table.lastModifiedOn || table.createdOn || '',
        columnsCount: table.columns?.count || 0,
        columnsPiiCount: table.columnsPii?.count || 0,
        noScanItemsCount: table.columnsNoScan?.count || 0,
        schemaId: table.schema?.edges[0]?.node?.id || '',
        schemaName: table.schema?.edges[0]?.node?.name || ''
      }))
    }
    const databases = raw.datasources?.edges[0]?.node?.databases || raw.databases
    const tables = databases.edges
      .map(({ node: db }) => {
        const databaseId = db.id
        const databaseName = db.name
        return db.tableClusters
          ? db.tableClusters?.edges
              ?.map(({ node: cl }) => {
                return mapTablesList({
                  tables: cl.tables,
                  databaseId,
                  databaseName,
                  clusterId: cl.id,
                  clusterName: cl.name
                })
              })
              .flat()
          : mapTablesList({ tables: db.tables, databaseId, databaseName })
      })
      .flat()

    return { list: tables, total: tables?.length || 0 }
  } catch (error) {
    console.error(error)
    throw error
  }
}
